import type { ContextInput, KnownIssueCard, PatternFinding } from '@innspel/core';
import { useId, useMemo, useState, type KeyboardEvent } from 'react';
import { Ikon } from '../Ikon.tsx';
import { H, Hint } from './primitiver.tsx';
import { Btn } from './primitiver.tsx';
import { tekst } from './tekster.ts';

/**
 * 2d + 2e — feltet og kontekstlinja. Ett felt, ingenting annet.
 *
 * DD-44: send er inaktiv under `minTextLength`, og det er det ENESTE kravet.
 * DD-45: placeholderen stiller to spørsmål i feilsporet. DD-46: mønstervarselet
 * vises FØR sending og fjerner aldri noe selv. DD-05: konteksten er synlig og
 * fjernbar linje for linje. Ctrl+Enter sender (wireframe 2d).
 *
 * Presisert 2026-09-14: **Send bor i dialogens fot** (C2), ikke her — den lå
 * sist i stabelen og forsvant under folden med bilde og kontekst åpent. Steget
 * har en overskrift, og telleren er `polite`: `assertive` avbrøt skrivingen for
 * en skjermleserbruker ved hvert tastetrykk over 80 % (C11e).
 */
export function Felt({
  spor,
  verdi,
  onEndre,
  minTextLength,
  textMaxLength,
  kontekst,
  fjernedeFelter,
  onFjernKontekst,
  lenketTil,
  onFjernLenke,
  funn,
  onSend,
  kanSende,
  onTaBilde,
  onLastOppBilde,
  bildeNotat,
  harVedlegg,
  vedleggFelt,
  onFjernVedlegg,
  etiketter = tekst.kontekstEtiketter,
}: {
  spor: 'feil' | 'onske';
  verdi: string;
  onEndre: (v: string) => void;
  minTextLength: number;
  textMaxLength: number;
  kontekst: ContextInput & { sdkVersion: string };
  fjernedeFelter: string[];
  onFjernKontekst: (felt: string) => void;
  lenketTil?: KnownIssueCard;
  onFjernLenke?: () => void;
  funn: PatternFinding[];
  onSend: () => void;
  /** Ctrl+Enter sender bare når foten ville gjort det (DD-44). */
  kanSende: boolean;
  /** Skjermbilde-steget (2f, K4). Utelatt = steget finnes ikke (DD-26). Aldri en deaktivert knapp. */
  onTaBilde?: () => void;
  onLastOppBilde?: (fil: File) => void;
  /** Vertens setning ved bildesteget (`imageStepNote`). Står bare der steget finnes. */
  bildeNotat?: string;
  harVedlegg?: boolean;
  /** Hvor mange felter som var dekket i bildet. 0 = chipen teller ingenting (C9). */
  vedleggFelt?: number;
  onFjernVedlegg?: () => void;
  /** The labels of the context lines. The TV's context after a handoff has its own (S1.3). */
  etiketter?: Record<string, string>;
}) {
  const [beholdt, setBeholdt] = useState<string[]>([]);
  const feltId = useId();
  const filId = useId();

  const brukt = [...verdi].length;
  const forKort = brukt < minTextLength;
  const full = brukt >= textMaxLength;
  const visTeller = brukt >= textMaxLength * 0.8;
  const uavklart = funn.find((f) => !beholdt.includes(f.match));

  const linjer = useMemo(() => {
    // `sdkVersion` står ikke her: den sendes, men «SDK» er repoets ord og sier
    // innsenderen ingenting (DD-50, presisert 2026-09-14).
    const rekkefølge = ['screen', 'os', 'locale', 'appVersion', 'build'];
    return rekkefølge
      .map((felt) => {
        const v = (kontekst as unknown as Record<string, string | undefined>)[felt];
        return v && !fjernedeFelter.includes(felt) ? { felt, etikett: etiketter[felt] ?? felt, verdi: v } : null;
      })
      .filter((x): x is { felt: string; etikett: string; verdi: string } => x !== null);
  }, [kontekst, fjernedeFelter, etiketter]);

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && kanSende) {
      e.preventDefault();
      onSend();
    }
  };

  const etikett = spor === 'onske' ? tekst.feltLabelOnske : tekst.feltLabelFeil;
  const chipTekst = vedleggFelt && vedleggFelt > 0 ? tekst.bildeLagtVedMedFelt(vedleggFelt) : tekst.bildeLagtVed;

  return (
    <div className="innspel-stabel">
      <H>{tekst.dialogSteg.felt!}</H>

      {lenketTil ? (
        <div className="innspel-chip" role="group" aria-label={tekst.lenketTil(lenketTil.title)}>
          <Ikon navn="link-outline" />
          <span>{tekst.lenketTil(lenketTil.title)}</span>
          <button type="button" className="innspel-ikonknapp" data-testid="fjern-lenke" aria-label={tekst.fjernLenke} onClick={onFjernLenke}>
            <Ikon navn="close-outline" />
          </button>
        </div>
      ) : null}

      {uavklart ? (
        <div className="innspel-varsel" role="status" aria-live="polite" data-testid="monstervarsel">
          <p>
            <Ikon navn="warning-outline" />{' '}
            {tekst.monsterSpm[uavklart.kind]}
          </p>
          <div className="innspel-rad">
            <Btn grow variant="secondary" testId="monster-fjern" label={tekst.monsterFjern} onClick={() => onEndre(verdi.slice(0, uavklart.start) + verdi.slice(uavklart.end))} />
            <Btn grow variant="ghost" testId="monster-behold" label={tekst.monsterBehold} onClick={() => setBeholdt((b) => [...b, uavklart.match])} />
          </div>
          <Hint>{tekst.monsterForklaring}</Hint>
        </div>
      ) : null}

      <div className="innspel-felt">
        <label className="innspel-felt-etikett" htmlFor={feltId}>
          {etikett}
        </label>
        <textarea
          id={feltId}
          className="innspel-tekstfelt"
          data-testid="innspill-tekst"
          data-full={full ? 'true' : undefined}
          value={verdi}
          onChange={(e) => onEndre(e.target.value)}
          onKeyDown={onKey}
          maxLength={textMaxLength}
          rows={4}
          placeholder={spor === 'onske' ? tekst.placeholderOnske : tekst.placeholderFeil}
          aria-describedby={forKort ? `${feltId}-hint` : undefined}
        />
        {visTeller ? (
          <span className="innspel-teller" data-testid="teller" data-full={full ? 'true' : undefined} role="status" aria-live="polite">
            {full ? <Ikon navn="warning-outline" /> : null}
            {brukt} / {textMaxLength}
            {full ? tekst.tellerFull : ''}
          </span>
        ) : null}
      </div>

      {/* 2f — opt-in per innsending, to veier på web. Bildet lages først ved trykk (K4). */}
      {harVedlegg ? (
        <div className="innspel-chip" data-testid="vedlegg-chip" role="group" aria-label={chipTekst}>
          <Ikon navn="image-outline" />
          <span>{chipTekst}</span>
          <button type="button" className="innspel-ikonknapp" data-testid="fjern-vedlegg" aria-label={tekst.fjernSkjermbilde} onClick={onFjernVedlegg}>
            <Ikon navn="close-outline" />
          </button>
        </div>
      ) : onTaBilde || onLastOppBilde ? (
        <div className="innspel-stabel" data-gap="sm">
          <div className="innspel-rad">
            {onTaBilde ? <Btn grow variant="secondary" testId="ta-bilde" ikon="camera-outline" label={tekst.taBildeAvSiden} onClick={onTaBilde} /> : null}
            {onLastOppBilde ? (
              <div className="innspel-knapp-ramme" data-grow="true">
                <label className="innspel-knapp" data-variant="secondary" htmlFor={filId}>
                  <Ikon navn="folder-outline" />
                  {tekst.lastOppBilde}
                </label>
                <input
                  id={filId}
                  className="innspel-skjult-fil"
                  data-testid="last-opp"
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(e) => {
                    const fil = e.target.files?.[0];
                    if (fil) onLastOppBilde(fil);
                    e.target.value = '';
                  }}
                />
              </div>
            ) : null}
          </div>
          <Hint>{tekst.skjermbildeHint}</Hint>
          {bildeNotat ? (
            <div data-testid="bilde-notat">
              <Hint>{bildeNotat}</Hint>
            </div>
          ) : null}
        </div>
      ) : null}

      {/*
        2e — synlig, ikke skjult (DD-05). R7.3 (portal-konsept v0.1): lista står ÅPEN.
        «Vis»/«Skjul» var en bryter foran det eneste som gjør K5 sant — at innsenderen ser
        hva som sendes med, og kan fjerne en linje før hun sender. Et lukket `aria-expanded`
        er ikke «synlig». Etiketten står som overskrift i stedet for som knapp.
      */}
      <div className="innspel-kontekst">
        <p className="innspel-kontekst-tittel" data-testid="kontekst-tittel">
          <Ikon navn="information-circle-outline" />
          {tekst.kontekstLinje}
        </p>
        {linjer.length > 0 ? (
          <ul className="innspel-liste" style={{ gap: 0 }}>
            {linjer.map((l) => (
              <li key={l.felt} className="innspel-kontekst-linje">
                <span className="innspel-kontekst-nokkel">{l.etikett}</span>
                <span className="innspel-kontekst-verdi">{l.verdi}</span>
                <button type="button" className="innspel-ikonknapp" data-testid={`fjern-${l.felt}`} aria-label={tekst.kontekstFjernA11y(l.etikett)} onClick={() => onFjernKontekst(l.felt)}>
                  <Ikon navn="close-outline" />
                  {tekst.kontekstFjern}
                </button>
              </li>
            ))}
            <li>
              <Hint>{tekst.kontekstAldri}</Hint>
            </li>
          </ul>
        ) : null}
      </div>

      {forKort ? (
        <span id={`${feltId}-hint`} className="innspel-sr">
          {tekst.forKort(minTextLength)}
        </span>
      ) : null}
    </div>
  );
}
